import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Eye,
  Send,
  XCircle,
  Download,
  Clock,
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  FileSignature
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../../lib/api';
import { Card, CardContent, Button, Badge } from '../../../components/ui';

type ContractStatus = 'DRAFT' | 'PENDING_SIGNATURES' | 'PARTIALLY_SIGNED' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';

interface DigitalContract {
  id: string;
  templateId: string;
  status: ContractStatus;
  verificationCode: string;
  createdAt: string;
  sentAt?: string;
  completedAt?: string;
  expiresAt?: string;
  template?: {
    name: string;
    category: string;
  };
  _count?: {
    signatures: number;
  };
  signatures?: {
    status: string;
  }[];
}

const STATUS_CONFIG: Record<ContractStatus, { icon: typeof CheckCircle; bgColor: string; textColor: string; label: string }> = {
  DRAFT: { icon: FileText, bgColor: 'bg-surface-700', textColor: 'text-surface-300', label: 'Rascunho' },
  PENDING_SIGNATURES: { icon: Clock, bgColor: 'bg-yellow-500/20', textColor: 'text-yellow-400', label: 'Aguardando' },
  PARTIALLY_SIGNED: { icon: AlertTriangle, bgColor: 'bg-blue-500/20', textColor: 'text-blue-400', label: 'Parcial' },
  COMPLETED: { icon: CheckCircle, bgColor: 'bg-green-500/20', textColor: 'text-green-400', label: 'Concluído' },
  CANCELLED: { icon: XCircle, bgColor: 'bg-red-500/20', textColor: 'text-red-400', label: 'Cancelado' },
  EXPIRED: { icon: AlertTriangle, bgColor: 'bg-surface-700', textColor: 'text-surface-400', label: 'Expirado' },
};

export function ContractList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContractStatus | ''>('');
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const { data: contracts, isLoading } = useQuery({
    queryKey: ['contracts', search, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get<DigitalContract[]>(`/contracts?${params}`);
      return response.data;
    },
  });

  const sendMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/contracts/${id}/send`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/contracts/${id}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });

  const handleSend = (id: string) => {
    sendMutation.mutate(id);
    setOpenMenu(null);
  };

  const handleCancel = (id: string) => {
    if (confirm('Tem certeza que deseja cancelar este contrato?')) {
      cancelMutation.mutate(id);
    }
    setOpenMenu(null);
  };

  const getSignatureProgress = (contract: DigitalContract) => {
    if (!contract.signatures) return { signed: 0, total: 0 };
    const signed = contract.signatures.filter(s => s.status === 'SIGNED').length;
    return { signed, total: contract.signatures.length };
  };

  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/contracts" className="p-2 hover:bg-surface-800 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-surface-400" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500/20 rounded-xl">
                <FileSignature className="w-5 h-5 text-primary-400" />
              </div>
              <h1 className="text-2xl font-bold text-surface-100">Contratos</h1>
            </div>
            <p className="text-surface-400 mt-1 ml-12">Gerencie os contratos do seu tenant</p>
          </div>
        </div>
        <Link to="/admin/contracts/new">
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Novo Contrato
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
                <input
                  type="text"
                  placeholder="Buscar contratos..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-surface-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ContractStatus | '')}
                className="px-3 py-2.5 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Todos os status</option>
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contracts Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-800/50 border-b border-surface-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                  Contrato
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                  Assinaturas
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                  Criado em
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-700">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4" />
                    <p className="text-surface-400">Carregando...</p>
                  </td>
                </tr>
              ) : contracts?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-surface-600" />
                    <p className="text-surface-400">Nenhum contrato encontrado</p>
                    <Link to="/admin/contracts/new">
                      <Button variant="outline" size="sm" className="mt-4">
                        Criar primeiro contrato
                      </Button>
                    </Link>
                  </td>
                </tr>
              ) : (
                contracts?.map((contract) => {
                  const statusConfig = STATUS_CONFIG[contract.status];
                  const StatusIcon = statusConfig.icon;
                  const progress = getSignatureProgress(contract);

                  return (
                    <tr key={contract.id} className="hover:bg-surface-800/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-surface-800 rounded-lg">
                            <FileText className="w-5 h-5 text-surface-400" />
                          </div>
                          <div>
                            <p className="font-medium text-surface-100">
                              #{contract.verificationCode.slice(0, 8)}
                            </p>
                            <p className="text-xs text-surface-500">
                              ID: {contract.id.slice(0, 8)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-surface-100">{contract.template?.name || '-'}</p>
                        <p className="text-xs text-surface-500">{contract.template?.category}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-2 bg-surface-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 transition-all"
                              style={{ width: progress.total > 0 ? `${(progress.signed / progress.total) * 100}%` : '0%' }}
                            />
                          </div>
                          <span className="text-sm text-surface-400">
                            {progress.signed}/{progress.total}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm text-surface-400">
                          <Clock className="w-4 h-4" />
                          {format(new Date(contract.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenu(openMenu === contract.id ? null : contract.id)}
                            className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                          >
                            <MoreVertical className="w-4 h-4 text-surface-400" />
                          </button>

                          {openMenu === contract.id && (
                            <>
                              <div 
                                className="fixed inset-0 z-10" 
                                onClick={() => setOpenMenu(null)}
                              />
                              <div className="absolute right-0 mt-2 w-48 bg-surface-800 rounded-lg shadow-xl border border-surface-700 z-20">
                                <Link
                                  to={`/admin/contracts/${contract.id}`}
                                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-surface-200 hover:bg-surface-700 transition-colors"
                                >
                                  <Eye className="w-4 h-4" />
                                  Visualizar
                                </Link>
                                {contract.status === 'DRAFT' && (
                                  <button
                                    onClick={() => handleSend(contract.id)}
                                    className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-surface-200 hover:bg-surface-700 transition-colors"
                                  >
                                    <Send className="w-4 h-4" />
                                    Enviar para assinatura
                                  </button>
                                )}
                                <Link
                                  to={`/contracts/${contract.id}/pdf`}
                                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-surface-200 hover:bg-surface-700 transition-colors"
                                >
                                  <Download className="w-4 h-4" />
                                  Download PDF
                                </Link>
                                {(contract.status === 'DRAFT' || contract.status === 'PENDING_SIGNATURES') && (
                                  <>
                                    <div className="border-t border-surface-700 my-1" />
                                    <button
                                      onClick={() => handleCancel(contract.id)}
                                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                                    >
                                      <XCircle className="w-4 h-4" />
                                      Cancelar
                                    </button>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
